import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  WrenchIcon
} from '@heroicons/react/24/outline';

export default function WhatsAppPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testPhone, setTestPhone] = useState('+919876543210');
  const [testMsg, setTestMsg] = useState('Hello! Welcome to our Gym. Your check-in was successful!');
  const [statusMsg, setStatusMsg] = useState('');

  const [formData, setFormData] = useState({
    phone_number_id: '',
    whatsapp_business_account_id: '',
    access_token: '',
    is_enabled: true,
    auto_send_checkin: true,
    auto_send_receipt: true,
    auto_send_expiry: true,
    template_checkin: 'Hello {member_name}, welcome to our gym! Checked in at {check_in_time}.',
    template_receipt: 'Hello {member_name}, thank you for your payment of {amount}. Receipt ID: {receipt_id}.'
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/whatsapp/config');
      if (res.data.config) {
        setConfig(res.data.config);
        setFormData(res.data.config);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/api/whatsapp/config', formData);
      setConfig(res.data.config);
      setStatusMsg('WhatsApp Business API settings saved successfully!');
      setTimeout(() => setStatusMsg(''), 5000);
    } catch (err) {
      alert('Failed to save WhatsApp configuration');
    }
  };

  const handleSendTestMessage = async () => {
    try {
      const res = await api.post('/api/whatsapp/send-message', {
        phone: testPhone,
        message: testMsg
      });
      setStatusMsg(res.data.message);
      setTimeout(() => setStatusMsg(''), 5000);
    } catch (err) {
      alert('Failed to dispatch test WhatsApp message');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="w-8 h-8 text-green-600" />
          WhatsApp Auto Alerts & Notifications Hub
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Automate instant WhatsApp notifications for member check-ins, payment receipts, and plan expiration warnings.
        </p>
      </div>

      {statusMsg && (
        <div className="bg-green-100 border border-green-300 text-green-800 p-4 rounded-2xl text-xs font-bold flex items-center gap-2">
          <CheckCircleIcon className="w-5 h-5 text-green-600" />
          {statusMsg}
        </div>
      )}

      {/* Main Grid */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* API Settings */}
        <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm space-y-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <WrenchIcon className="w-5 h-5 text-green-600" /> WhatsApp Cloud API Settings
          </h2>

          <form onSubmit={handleSaveConfig} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">WhatsApp Phone Number ID</label>
              <input
                type="text"
                placeholder="e.g. 10988273928172"
                value={formData.phone_number_id || ''}
                onChange={(e) => setFormData({ ...formData, phone_number_id: e.target.value })}
                className="w-full text-sm border p-2.5 rounded-xl font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">WhatsApp Business Account ID</label>
              <input
                type="text"
                placeholder="e.g. 9982731827"
                value={formData.whatsapp_business_account_id || ''}
                onChange={(e) => setFormData({ ...formData, whatsapp_business_account_id: e.target.value })}
                className="w-full text-sm border p-2.5 rounded-xl font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">System Access Token (Permanent Token)</label>
              <textarea
                rows={3}
                placeholder="EAAG..."
                value={formData.access_token || ''}
                onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
                className="w-full text-xs border p-2.5 rounded-xl font-mono"
              />
            </div>

            {/* Toggles */}
            <div className="space-y-2 bg-gray-50 p-4 rounded-xl border border-gray-200">
              <h3 className="text-xs font-bold text-gray-700 uppercase mb-2">Automated Event Triggers</h3>
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.auto_send_checkin}
                  onChange={(e) => setFormData({ ...formData, auto_send_checkin: e.target.checked })}
                  className="rounded text-green-600 focus:ring-green-500 w-4 h-4"
                />
                Auto-send Check-in Welcome Message
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.auto_send_receipt}
                  onChange={(e) => setFormData({ ...formData, auto_send_receipt: e.target.checked })}
                  className="rounded text-green-600 focus:ring-green-500 w-4 h-4"
                />
                Auto-send Digital Payment Receipt
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.auto_send_expiry}
                  onChange={(e) => setFormData({ ...formData, auto_send_expiry: e.target.checked })}
                  className="rounded text-green-600 focus:ring-green-500 w-4 h-4"
                />
                Auto-send Plan Expiration Reminder
              </label>
            </div>

            <button
              type="submit"
              className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl shadow-sm transition-all text-sm"
            >
              Save WhatsApp Configuration
            </button>
          </form>
        </div>

        {/* Test Dispatcher */}
        <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm space-y-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <PaperAirplaneIcon className="w-5 h-5 text-green-600" /> Send Instant Test WhatsApp Message
          </h2>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Recipient Mobile Number (With Country Code)</label>
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                className="w-full text-sm border p-2.5 rounded-xl font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Message Content</label>
              <textarea
                rows={4}
                value={testMsg}
                onChange={(e) => setTestMsg(e.target.value)}
                className="w-full text-sm border p-2.5 rounded-xl"
              />
            </div>

            <button
              onClick={handleSendTestMessage}
              className="w-full py-3 bg-gray-900 hover:bg-black text-white font-bold rounded-xl shadow-sm transition-all text-sm flex items-center justify-center gap-2"
            >
              <PaperAirplaneIcon className="w-4 h-4 text-green-400" /> Dispatch Test Message
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
